package com.example.mystybag.core.expected

import com.example.mystybag.core.security.Security
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.launch

internal expect object PreferencesImpl {

    fun setInt(key: String, value: Int)
    fun getInt(key: String, defaultValue: Int): Int
    fun getInt(key: String): Int?

    fun setFloat(key: String, value: Float)
    fun getFloat(key: String, defaultValue: Float): Float
    fun getFloat(key: String): Float?

    fun setLong(key: String, value: Long)
    fun getLong(key: String, defaultValue: Long): Long
    fun getLong(key: String): Long?

    fun setString(key: String, value: String)
    fun getString(key: String, defaultValue: String): String
    fun getString(key: String): String?

    fun setBoolean(key: String, value: Boolean)
    fun getBoolean(key: String, defaultValue: Boolean): Boolean
    fun getBoolean(key: String): Boolean?

    fun getStringSet(key: String, defaultValue:  Set<String>?): Set<String>?
    fun setStringSet(key: String, values: Set<String>)

    fun remove(key: String)
    fun clear()

    fun hasKey(key: String): Boolean
}

data class PreferenceValue<T: Any>(
    val key     : String,
    val value   : T?
)

object Preferences {

    //uncomment when needed
    private val stringFlow       = MutableSharedFlow<PreferenceValue<String>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    private val booleanFlow      = MutableSharedFlow<PreferenceValue<Boolean>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    private val longFlow         = MutableSharedFlow<PreferenceValue<Long>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    //private val intFlow          = MutableSharedFlow<PreferenceValue<Int>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    //private val floatFlow        = MutableSharedFlow<PreferenceValue<Float>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST)
    //private val stringSetFlow    = MutableSharedFlow<PreferenceValue<Set<String>>>(extraBufferCapacity = 5, onBufferOverflow = BufferOverflow.DROP_OLDEST) */

    fun setInt(key: String, value: Int) {
        PreferencesImpl.setInt(key, value)
        //intFlow.tryEmit(PreferenceValue(key, value))
    }
    fun getInt(key: String, defaultValue: Int): Int = PreferencesImpl.getInt(key, defaultValue)
    fun getInt(key: String): Int? = PreferencesImpl.getInt(key)

    /*fun setFloat(key: String, value: Float) {
        PreferencesImpl.setFloat(key, value)
        //floatFlow.tryEmit(PreferenceValue(key, value))
    }
    fun getFloat(key: String, defaultValue: Float): Float = PreferencesImpl.getFloat(key, defaultValue)
    fun getFloat(key: String): Float? = PreferencesImpl.getFloat(key)*/

    fun setLong(key: String, value: Long) {
        PreferencesImpl.setLong(key, value)
        longFlow.tryEmit(PreferenceValue(key, value))
    }
    fun getLong(key: String, defaultValue: Long): Long = PreferencesImpl.getLong(key, defaultValue)
    fun getLong(key: String): Long? = PreferencesImpl.getLong(key)

    fun setString(key: String, value: String) {
        PreferencesImpl.setString(key, value)
        stringFlow.tryEmit(PreferenceValue(key, value))
    }
    fun getString(key: String, defaultValue: String): String = PreferencesImpl.getString(key, defaultValue)
    fun getString(key: String): String? = PreferencesImpl.getString(key)

    fun setSecureString(key: String, value: String): Boolean {
        if (value.isEmpty()) {
            remove(key)
            return false
        }

        Security.checkKeys()
        val hadEncryptedValueBefore = getString(key, "").isNotEmpty()

        val encrypted = Security.secureEncryptPreference(value)
        if (encrypted.isEmpty()) {
            remove(key)
            return false
        }

        setString(key, encrypted)
        stringFlow.tryEmit(PreferenceValue(key, value))
        return true
    }
    fun getSecureString(key: String, defaultValue: String): String = getSecureString(key) ?: defaultValue

    fun getSecureString(key: String): String? {
        Security.checkKeys()
        val raw = getString(key)
        if (raw.isNullOrEmpty()) {
            return null
        }

        val decrypted = Security.secureDecryptPreference(raw)
        return decrypted
    }

    fun getSecureByteArray(key: String): ByteArray? = getString(key)?.let { Security.secureDecryptByteArray(it) }

    fun setBoolean(key: String, value: Boolean) {
        PreferencesImpl.setBoolean(key, value)
        booleanFlow.tryEmit(PreferenceValue(key, value))
    }
    fun getBoolean(key: String, defaultValue: Boolean): Boolean = PreferencesImpl.getBoolean(key, defaultValue)
    fun getBoolean(key: String): Boolean? = PreferencesImpl.getBoolean(key)

    fun setStringSet(key: String, values: Set<String>) {
        PreferencesImpl.setStringSet(key, values)
        //stringSetFlow.tryEmit(PreferenceValue(key, values))
    }
    fun getStringSet(key: String, defaultValue:  Set<String>?): Set<String>? = PreferencesImpl.getStringSet(key, defaultValue)

    fun remove(key: String) {
        PreferencesImpl.remove(key)
        stringFlow.tryEmit(PreferenceValue(key, null))
        booleanFlow.tryEmit(PreferenceValue(key, null))
        longFlow.tryEmit(PreferenceValue(key, null))
        //intFlow.tryEmit(PreferenceValue(key, null))
        //floatFlow.tryEmit(PreferenceValue(key, null))
        //stringSetFlow.tryEmit(PreferenceValue(key, null))
    }

    fun clear() {
        PreferencesImpl.clear()
    }

    fun hasKey(key: String): Boolean = PreferencesImpl.hasKey(key)

    fun observeString(key: String, defaultValue: String? = null) = channelFlow {
        trySend(PreferenceValue(key, PreferencesImpl.getString(key) ?: defaultValue))

        val job = launch {
            stringFlow
                .filter { it.key == key }
                .collectLatest { trySend(it) }
        }

        awaitClose {
            job.cancel()
        }
    }

    fun observeBoolean(key: String, defaultValue: Boolean? = null) = channelFlow {
        trySend(PreferenceValue(key, PreferencesImpl.getBoolean(key) ?: defaultValue))

        val job = launch {
            booleanFlow
                .filter { it.key == key }
                .collectLatest { trySend(it) }
        }

        awaitClose {
            job.cancel()
        }
    }

    fun observeLong(key: String, defaultValue: Long? = null) = channelFlow {
        trySend(PreferenceValue(key, PreferencesImpl.getLong(key) ?: defaultValue))

        val job = launch {
            longFlow
                .filter { it.key == key }
                .collectLatest { trySend(it) }
        }

        awaitClose {
            job.cancel()
        }
    }

    /*fun observeInt(key: String, defaultValue: Int? = null) = channelFlow {
        send(PreferenceValue(key, PreferencesImpl.getInt(key) ?: defaultValue))
        intFlow.filter { it.key == key }.collectLatest {
            send(it)
        }
    }

    fun observeFloat(key: String, defaultValue: Float? = null) = channelFlow {
        send(PreferenceValue(key, PreferencesImpl.getFloat(key) ?: defaultValue))
        floatFlow.filter { it.key == key }.collectLatest {
            send(it)
        }
    }

    fun observeStringSet(key: String, defaultValue: Set<String>? = null) = channelFlow {
        send(PreferenceValue(key, PreferencesImpl.getStringSet(key, defaultValue)))
        stringSetFlow.filter { it.key == key }.collectLatest {
            send(it)
        }
    } */
}